import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { functions } from "src/api/firebase";

// -----------------------------
// Type Definitions
// -----------------------------
export interface InterviewRecording {
    id: string;
    sessionId: string;
    url: string;
    mimeType: string;
    uploadTime: string | null;
    fileSize: number;
    duration: number;
    questionIndex: number;
}

interface BackendRecording {
    id: string;
    sessionId: string;
    storagePath: string;
    firebaseToken: string;
    mimeType: string;
    uploadTime: string | null;
    fileSize: number;
    duration: number;
    questionIndex: number;
}

interface RecordingsResponse {
    recordings: BackendRecording[];
}

// -----------------------------
// Firebase Callable Function
// -----------------------------
const getInterviewRecordings = httpsCallable<
    { sessionId?: string },
    RecordingsResponse
>(functions, "getInterviewRecordings");

// -----------------------------
// Data Fetching Function
// -----------------------------
const fetchInterviewRecordings = async (
    sessionId?: string
): Promise<InterviewRecording[]> => {
    try {
        const response = await getInterviewRecordings({ sessionId });
        const backendRecordings = response.data.recordings || [];

        // If no recordings, return empty array
        if (backendRecordings.length === 0) {
            return [];
        }

        // Use the first recording's token to authenticate (they all have the same token)
        const storage = getStorage();

        // Process each recording
        const processedRecordings = await Promise.all(
            backendRecordings.map(async (recording) => {
                try {
                    // Get the download URL using Firebase Storage SDK
                    const url = await getDownloadURL(
                        ref(storage, recording.storagePath)
                    );
                    return {
                        id: recording.id,
                        sessionId: recording.sessionId,
                        url: url,
                        mimeType: recording.mimeType || "audio/webm",
                        uploadTime: recording.uploadTime,
                        fileSize: recording.fileSize || 0,
                        duration: recording.duration || 0,
                        questionIndex: recording.questionIndex,
                    };
                } catch (error) {
                    console.error(
                        `Error getting URL for ${recording.storagePath}:`,
                        error
                    );
                    // Return a placeholder URL for failed recordings
                    return {
                        id: recording.id,
                        sessionId: recording.sessionId,
                        url: "", // Empty URL indicates it failed to load
                        mimeType: recording.mimeType || "audio/webm",
                        uploadTime: recording.uploadTime,
                        fileSize: recording.fileSize || 0,
                        duration: recording.duration || 0,
                        questionIndex: recording.questionIndex,
                    };
                }
            })
        );

        return processedRecordings;
    } catch (error) {
        console.error("Error fetching interview recordings", error);
        return [];
    }
};

// -----------------------------
// Custom React Query Hook
// -----------------------------
export function useFetchInterviewRecordings(sessionId?: string) {
    const query = useQuery<InterviewRecording[]>({
        queryKey: ["interviewRecordings", sessionId],
        queryFn: () => fetchInterviewRecordings(sessionId),
    });

    return {
        recordings: query.data || [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
